import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { AuthLayout } from './AuthLayout.js'

export function OAuthCallback() {
  usePageTitle('Signing you in')
  const { api, signIn } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    const code = params.get('code')
    if (code === null || code === '') {
      setError('That sign-in link is incomplete. Start again from the sign-in page.')
      return
    }
    // The code is single use; strict mode runs effects twice in development and the
    // second call would consume an already-spent code and show a spurious error.
    if (started.current) return
    started.current = true

    void (async () => {
      try {
        const result = await api.post<{ accessToken: string; refreshToken: string }>('/api/auth/oauth/exchange', { code })
        await signIn(result)
        navigate('/', { replace: true })
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.')
      }
    })()
  }, [api, navigate, params, signIn])

  return (
    <AuthLayout
      title={error === null ? 'Signing you in' : 'Sign-in failed'}
      subtitle={error ?? 'One moment while we finish signing you in.'}
      footer={error === null ? undefined : <Link to="/login">← Back to sign in</Link>}
    >
      {error === null ? <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Completing sign-in…</p> : null}
    </AuthLayout>
  )
}
